import { Answer, Option } from "@quark/shared-types";
import { ExamConfig } from "./exam";
import { Season } from "./season";

export type Numerical = number | string;

export type ComponentType = "QuizRef" | "Markdown" | "ChallengeRef";

export type PageStatus = "undo" | "done" | "lock";

export type QuizRefContent = {
  version: string;
  componentId: string;
  title: string;
  questionId: Numerical;
  status: PageStatus;
};

export type MarkdownContent = Omit<QuizRefContent, "questionId"> & {
  knowledgeId: Numerical;
};

export type ChallengeRefContent = Omit<QuizRefContent, "questionId"> & {
  challengeId: Numerical;
};

export type Content<T extends ComponentType> = T extends "QuizRef"
  ? QuizRefContent
  : T extends "Markdown"
  ? MarkdownContent
  : T extends "ChallengeRef"
  ? ChallengeRefContent
  : never;

export type PageComponent<T extends ComponentType> = {
  type: T;
  step: number;
  content: Content<T>;
};

export type Page = {
  id: Numerical;
  title: string;
  summary: string;
  tags: string;
  status: PageStatus;
  number?: string;
  isPreview?: boolean;
  coaches?: Omit<Page, "components">[];
  components: PageComponent<ComponentType>[];
};

export type KnowledgeType =
  | "MarkdownEditor"
  | "Image"
  | "Note"
  | "Code"
  | "Coach"
  | "JsHtml";

export type KnowledgeMarkdownContent = {
  id: string;
  title: string;
  text: string;
};

export type KnowledgeCodeContent = {
  id: string;
  title: string;
  language: string;
  specialInput: string;
  entryFileName: string;
  content: string;
  shortContent: string;
  timeLimit: number;
  allowDownload: boolean;
  readonly: boolean;
  highlightLines: string;
  runnable: boolean;
  judge: boolean;
  judgeContentPrepend: string;
  showSolution: boolean;
  solutionContent: string;
  additionalContent: any[];
  judgeContent: string | null;
  judgeHints: boolean | null;
};

export type KnowledgeNoteContent = {
  id: string;
  text: string;
};

export type KnowledgeImageContent = {
  src: string;
  width: number;
  height: number;
};

export type KnowledgeCoachContent = {
  id: Numerical;
  title: string;
  description: string;
  name: string;
};

export type JsHtmlContent = {
  htmlCode: string;
  jsCode: string;
  cssCode: string;
  language: string;
  runnable: boolean;
  showOutput: boolean;
};

export type KnowledgeContent<K extends KnowledgeType> =
  K extends "MarkdownEditor"
    ? KnowledgeMarkdownContent
    : K extends "Code"
    ? KnowledgeCodeContent
    : K extends "Note"
    ? KnowledgeNoteContent
    : K extends "Image"
    ? KnowledgeImageContent
    : K extends "Coach"
    ? KnowledgeCoachContent
    : K extends "JsHtml"
    ? JsHtmlContent
    : never;

export type Knowledge<K extends KnowledgeType> = {
  type: K;
  content: KnowledgeContent<K>;
  step: number;
};

export type QuestionType =
  | "drag_and_drop"
  | "single_choice"
  | "mult_choice"
  | "reorder"
  | "type_in"
  | "multi_type_in";

export type Question = {
  type: QuestionType;
  text: string;
  options: Option[];
  quizAnswer: Answer[];
  userAnswer?: Answer[];
  isRight?: boolean | null;
};

export type Quiz = {
  id: Numerical;
  title: string;
  knowledges?: Knowledge<KnowledgeType>[];
  question?: Question;
  challenge?: Record<string, any>;
  variant: "quiz" | "knowledge" | "challenge";
};

export type Slide = {
  type: "quiz-question" | "quiz-knowledge" | "edu-knowledge" | "edu-challenge";
  data:
    | (Question & { forceRender?: boolean })
    | Knowledge<KnowledgeType>[]
    | Record<string, any>;
} & Pick<Quiz, "id" | "title">;

export type TargetAudience = "beginner" | "intermediate" | "advanced";

export type Course = {
  id: number;
  course_no: string;
  title: string;
  summary: string;
  brief_summary: string;
  details: string;
  cover_img: string;
  intro_video_url: string | null;
  target_audience: TargetAudience;
  work_mode: string;
  is_mini: boolean;
  is_educative: boolean;
  is_active: boolean;
  read_hour: string;
  rank: number;
  skills: any[];
  cates: number[];
  toc: any;
  clos: any[];
  level_display: string;
  widget_stats: {
    lesson: number;
    quiz: number;
    challenge: number;
    playground: number;
    illustration: number;
  };
  learn_info: {
    coach_num: number;
    learn_coach_num: number;
    page_num: number;
    learn_page_num: number;
  };
  is_buy: boolean;
};

export type CourseCategory = {
  cate: string;
  courses: Course[];
};

export type BlogListItem = {
  id: number;
  cover: string;
  title: string;
  summary: string;
  author: string;
  pub_time: string;
  read_time: number;
};

export type CourseForOverview = {
  id: number;
  no: string;
  title: string;
  summary: string;
  courseIcon: string;
  widgets: {
    lesson: number;
    quiz: number;
    challenge: number;
    playground: number;
    illustration: number;
  };
  progress: {
    totalPageNumber: number;
    learnPageNumber: number;
    totalCoachNumber: number;
    learnCoachNumber: number;
  };
  isBuy: boolean;
  categories: {
    id: number;
    title: string;
    type: string;
    icon: string;
    pages: Omit<Page, "components">[];
    coaches: Omit<Page, "components">[];
    progress: {
      totalPageNumber: number;
      learnPageNumber: number;
      totalCoachNumber: number;
      learnCoachNumber: number;
    };
  }[];
};

export type UserInfo = {
  avatar: string;
  user_id: number;
  is_login: boolean;
  has_course: boolean;
  nickname: string;
  xp: number;
  bit: number;
  openid: string;
  server_time: string;
  vip_infos: any[];
  seasons: Season.Season[];
};

export type UserConfig = {
  exam: ExamConfig;
};

export type Xps = {
  // 学习积分，暂未使用
  xp: number;
  // 钻石积分
  bit: number;
};

export type VipInfo = {
  has_course: boolean;
  vip_infos: any[];
  course_list: {
    cate: string;
    courses: {
      title: string;
      cover: string;
      price: number;
    }[];
  }[];
};

export const PurchaseStatuses = {
  80: "CANCELLED",
  90: "REFUNDED",
  100: "SUBMIT",
  200: "PAYED_WAITING_FOR_CONFIRM",
  210: "PAYED",
  230: "WAITING_FOR_REFUND",
  300: "WAITING_FOR_SEND",
  310: "SENT",
  320: "RECEIVED",
  400: "FINISHED",
} as const;

export type ValueOf<T> = T[keyof T];
export type PurchaseStatus = ValueOf<typeof PurchaseStatuses>;
export type PurchaseStatusCode = keyof typeof PurchaseStatuses;
